import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationType } from '@bhops/shared';
import { notificationsApi, type NotificationDto } from '@/api/notifications';
import { useAuthStore } from '@/stores/auth';
import { kstYmd } from '@/lib/format-date';

// Plan §E-5-1 (PDF p.22) — in-app workflow alert UI.
//
// Compact bell rendered in the TopBar (right side). Clicking opens a
// dropdown panel anchored to the bell. Unread count is the only
// always-visible state; the panel itself queries on open to keep the
// row weight off the dashboard hot-path.
//
// Empty + loading + error states are explicit so a flaky API doesn't
// look like "you have no notifications" — the failure case shows a
// retry hint instead.

const TYPE_LABEL: Record<NotificationType, string> = {
  CONTRACT_ACTIVATED: '계약 활성화',
  CONTRACT_EXPIRING: '계약 만료 임박',
  INFRA_ORDER_CONTACTED: '고객사 연락 완료',
  INFRA_ORDER_DELIVERED: '그릴러 납품 완료',
  INFRA_ORDER_INSTALLED: '설치 완료',
  ROBOT_ASSIGNED: '로봇 할당',
  ROBOT_READY_TO_SHIP: '출고 준비 완료',
  REPORT_NEW: '신규 리포트',
  REPORT_SLA_BREACH: 'SLA 초과 리포트',
  // batch-15 §5.1 / §9.2 — Contract 사인 후 핸드오프 알람. shared/enums.ts
  // 에 추가됐지만 client label 이 누락돼 있던 5건. notifications drop-down
  // 에서 raw enum value 가 노출되던 문제 해결.
  CONTRACT_SIGNED_AWAITING_MFG_ASSIGNMENT: '계약 서명, 제조 배정 대기',
  CONTRACT_SIGNED_AWAITING_FIELD_INFRA: '계약 서명, 인프라 발주 대기',
  ROBOT_ASSIGNED_TO_CONTRACT: '로봇 계약 배정',
  PRODUCTION_PLAN_DELAYED: '제조 계획 지연',
  PRODUCTION_PLAN_COMPLETED: '제조 계획 완료',
  // 통합 S1/S2/S3 신규 4 type (unified plan §2). 기존 handoff 알람 재사용 외.
  CUSTOMER_RETURN_PLANNED: '회수 예정',
  ROBOT_ASSIGNMENT_REQUESTED: '로봇 할당 요청',
  INFRA_CONTACT_REQUESTED: '인프라 연락 요청',
  ROBOT_REPLACEMENT_REQUESTED: '로봇 교체 요청',
  // S2 Phase 2.5 — 배정 완료 → 필드 설치 일정 요청.
  ROBOT_INSTALL_SCHEDULE_REQUESTED: '설치 일정 요청',
};

export function NotificationBell() {
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Unread badge — runs always (cheap aggregate). Panel content is a
  // separate query that only fires when the panel is open.
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationsApi.list({ unreadOnly: true, limit: 50 }),
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const inboxQ = useQuery({
    queryKey: ['notifications', 'inbox'],
    queryFn: () => notificationsApi.list({ limit: 50 }),
    enabled: !!user && open,
    staleTime: 15_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // round3 #6 — 알림 삭제(개별 + 전체).
  const deleteOne = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteOne(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteAll = useMutation({
    mutationFn: () => notificationsApi.deleteAll(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = unreadQ.data?.length ?? 0;
  const inboxCount = inboxQ.data?.length ?? 0;

  function onItemClick(n: NotificationDto): void {
    if (!n.readAt) markRead.mutate(n.id);
    // S3 §3.5 (R2 #7) — 회수 예정 알림은 필드팀이 회수일을 확정하는 활성 고객사
    // 페이지로 직행(고객사 상세보다 작업 맥락에 맞음).
    if (n.type === NotificationType.CUSTOMER_RETURN_PLANNED) {
      navigate('/field/active-customers');
      setOpen(false);
      return;
    }
    // round4 #5c — 제조/필드 작업요청 알림은 payload.customerId 보다 '작업 화면'으로
    // 직행. (기존 customerId 우선 라우팅이 배정요청 등을 고객사 상세로 잘못 보냈음.)
    if (
      n.type === NotificationType.ROBOT_ASSIGNMENT_REQUESTED ||
      n.type === NotificationType.CONTRACT_SIGNED_AWAITING_MFG_ASSIGNMENT ||
      n.type === NotificationType.ROBOT_ASSIGNED_TO_CONTRACT
    ) {
      navigate('/manufacturing/assignments');
      setOpen(false);
      return;
    }
    if (
      n.type === NotificationType.ROBOT_INSTALL_SCHEDULE_REQUESTED ||
      n.type === NotificationType.CONTRACT_SIGNED_AWAITING_FIELD_INFRA ||
      n.type === NotificationType.INFRA_CONTACT_REQUESTED
    ) {
      navigate('/infrastructure');
      setOpen(false);
      return;
    }
    // Navigate based on payload — best-effort. Each trigger writes
    // a `dealId` / `contractId` / `robotId` / `reportId` into the
    // payload at publish time, so the bell can route the user
    // straight to the relevant detail page.
    const p = n.payload as {
      dealId?: string;
      contractId?: string;
      robotId?: string;
      reportId?: string;
      customerId?: string;
      infraOrderId?: string;
    };
    // 우선순위: 가장 구체적인 식별자부터. customerId/infraOrderId 는 통합
    // S1/S2/S3 신규 알람(회수/연락/할당/교체)의 라우팅. 목적지 페이지가
    // 후속 Phase 에서 완성되면 deep-link 정교화.
    // FB2 §3A — payload 에 customerId 가 있으면 contractId 보다 우선해
    // 고객사 상세로 직행한다(계약 상세는 redirect 경유라 한 단계 더 거침).
    // customerId 가 없는 contractId-only 알람은 종전대로 /contracts/:id 로
    // 보내고 redirect 가 받게 둔다. (알람 publisher 의 payload 보강은 별도 배치.)
    if (p.dealId) navigate(`/deals/${p.dealId}`);
    else if (p.customerId) navigate(`/customers/${p.customerId}`);
    else if (p.contractId) navigate(`/contracts/${p.contractId}`);
    else if (p.robotId) navigate(`/robots/${p.robotId}`);
    else if (p.infraOrderId) navigate('/infrastructure');
    else if (p.reportId) navigate('/service-orders?view=reports'); // round5 #14 — 탭 이식
    setOpen(false);
  }

  if (!user) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`알림, 안 읽은 알림 ${unreadCount}건`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="kuun-topbar-icon"
        style={bellStyle(open)}
      >
        {/* FB §9 — 무채색 모노 벨 아이콘 (currentColor = var(--fg-default)).
            기존 "알림" 텍스트 + 빨강 배지를 무채색으로 교체. aria-label 유지. */}
        <svg
          aria-hidden
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={badge} aria-hidden>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* invisible overlay to dismiss on outside click */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            onClick={() => setOpen(false)}
            style={overlay}
          />
          <div role="dialog" aria-label="알림 패널" style={panel}>
            <div style={panelHeader}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>알림</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                    style={readAllBtn}
                  >
                    {markAllRead.isPending ? '처리 중…' : '모두 읽음으로'}
                  </button>
                )}
                {/* round3 #6 — 전체 삭제. 파괴적이므로 확인 후 실행. */}
                {inboxCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('표시된 알림을 모두 삭제하시겠습니까?')) {
                        deleteAll.mutate();
                      }
                    }}
                    disabled={deleteAll.isPending}
                    style={deleteAllBtn}
                  >
                    {deleteAll.isPending ? '삭제 중…' : '모두 삭제'}
                  </button>
                )}
              </div>
            </div>
            <div style={panelBody}>
              {inboxQ.isLoading && <Empty text="불러오는 중…" />}
              {inboxQ.isError && <Empty text="알림을 불러오지 못했습니다." muted />}
              {inboxQ.data && inboxQ.data.length === 0 && <Empty text="알림이 없습니다." />}
              {inboxQ.data &&
                inboxQ.data.map((n) => (
                  // round3 #6 — 행 = [본문 버튼 | 삭제 버튼]. 기존엔 행 전체가
                  // <button> 이라 삭제 버튼을 중첩할 수 없었음 → div 컨테이너로 분리.
                  <div key={n.id} style={itemRow(!n.readAt)}>
                    <button type="button" onClick={() => onItemClick(n)} style={itemMainBtn}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: n.readAt ? 400 : 600,
                            color: 'var(--fg-default)',
                          }}
                        >
                          {TYPE_LABEL[n.type] ?? n.type}
                        </span>
                        {!n.readAt && <span aria-label="안 읽음" style={dot} />}
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontSize: 10,
                            color: 'var(--fg-muted)',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          {formatRelative(n.createdAt)}
                        </span>
                      </div>
                      {(n.payload as { message?: string }).message && (
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--fg-muted)',
                            marginTop: 4,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {(n.payload as { message?: string }).message}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="알림 삭제"
                      title="삭제"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOne.mutate(n.id);
                      }}
                      style={itemDeleteBtn}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Empty({ text, muted = false }: { text: string; muted?: boolean }) {
  return (
    <div
      style={{
        padding: '24px 16px',
        textAlign: 'center',
        fontSize: 12,
        color: muted ? 'var(--fg-danger)' : 'var(--fg-muted)',
      }}
    >
      {text}
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}일 전`;
  // 추가수정 2차(26-07-27) 정정3 — 7일 초과 폴백을 KST 표기로. toLocaleDateString
  // 은 브라우저 로캘·시간대에 따라 표기와 날짜가 흔들렸다(콘솔 표준은 점 표기).
  return kstYmd(d) ?? '–';
}

function bellStyle(active: boolean): CSSProperties {
  return {
    position: 'relative',
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--bg-sunken)' : 'transparent',
    border: '1px solid transparent',
    cursor: 'pointer',
    color: 'var(--fg-default)',
  };
}

const badge: CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  borderRadius: 8,
  // windows-ux-edit (2026-06, 슬라이드2) — 알림 배지를 다시 빨강으로(눈에 띄게).
  background: 'var(--fg-danger)',
  color: 'var(--brand-on)',
  fontSize: 9,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-mono)',
};

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99,
  background: 'transparent',
  border: 'none',
  cursor: 'default',
};

const panel: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  right: 0,
  width: 360,
  maxHeight: 480,
  background: 'var(--bg-surface)',
  // 색 상단 바의 흰 글자를 물려받아 제목 '알림'이 안 보였다(2026-10-22) — 창 안 글자색을 여기서 끊는다.
  color: 'var(--fg-default)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-lg)',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const panelHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: '1px solid var(--border-subtle)',
};

const panelBody: CSSProperties = {
  overflowY: 'auto',
  flex: 1,
};

const readAllBtn: CSSProperties = {
  fontSize: 11,
  color: 'var(--blue-800)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 6px',
};

// round3 #6 — 행 컨테이너(본문 버튼 + 삭제 버튼). 기존 itemStyle(단일 버튼) 대체.
function itemRow(unread: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    background: unread ? 'var(--blue-50)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
  };
}

const itemMainBtn: CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: 'left',
  padding: '10px 14px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

const itemDeleteBtn: CSSProperties = {
  flexShrink: 0,
  width: 36,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderLeft: '1px solid var(--border-subtle)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};

const deleteAllBtn: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-danger)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 6px',
};

const dot: CSSProperties = {
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--fg-danger)',
};
